import { POLYLINE, Cursors, CONNECTION, CONNECTION_POINTS_EDITING_SNAP } from '../../../constants';
import { Point } from '../../../math';
import { deepExtend } from '../../../utils/deep-extend';
import { Circle } from '../../../svg';
import { AdornerBase } from '../AdornerBase';
import { ConnectionDragUnit } from '../../ConnectionDragUnit';
export class PolylineConnectionResizeAdorner extends AdornerBase {
    constructor(connection, options) {
        super(connection.diagram, deepExtend({}, options));
        this._handlePoints = [];
        this.connection = connection;
        this.handles = [];
        this.activeHandle = null;
        this._firstMove = false;
        if (options) {
            this._createHandles();
        }
        this.diagram.bind('dblclick', this.onDblClick.bind(this));
    }
    onDblClick(e) {
        const handle = e.item.name === CONNECTION && this._hitTest(e.point);
        const handlePoint = handle && this._handlePoints.find(hp => hp.point.equals(handle.point));
        if (handlePoint && handlePoint.vertex) {
            const intermediate = this.connection.allPoints().slice(1, -1);
            const pointIndex = intermediate.findIndex(pt => pt.equals(handlePoint.point));
            if (pointIndex < 0) {
                return;
            }
            intermediate.splice(pointIndex, 1);
            this.connection.points(intermediate.map(pt => pt.clone()));
            this.connection.refresh();
            this.refresh();
        }
    }
    createHandlePoints() {
        const allPoints = this.connection.allPoints();
        const result = [];
        const source = allPoints[0];
        const target = allPoints[allPoints.length - 1];
        const intermediate = allPoints.slice(1, -1);
        const points = !intermediate.length ? [source, target] : [source, ...intermediate, target];
        for (let i = 0; i < points.length - 1;) {
            const j = i + 1;
            const pStart = points[i];
            const pEnd = points[j];
            if (pStart.x !== pEnd.x || pStart.y !== pEnd.y) {
                result.push({ vertex: true, point: pStart });
                result.push({ vertex: false, point: new Point((pStart.x + pEnd.x) / 2, (pStart.y + pEnd.y) / 2) });
                result.push({ vertex: true, point: pEnd });
            }
            i = j;
        }
        return result.slice(1, -1);
    }
    _createHandles() {
        this.handles.forEach((h) => this.visual.remove(h.visual));
        this.handles = [];
        if ((this.connection.options.type || '').toLowerCase() !== POLYLINE || this.connection.pointsEditable() === false) {
            return;
        }
        this._handlePoints = this.createHandlePoints();
        const handles = this.options;
        this._handlePoints.forEach(({ point: pt, vertex }) => {
            const handleVisual = new Circle({
                center: this.diagram.modelToLayer(pt),
                radius: vertex ? handles.vertex.radius : handles.midpoint.radius,
                fill: vertex ? handles.vertex.fill : handles.midpoint.fill,
                stroke: vertex ? handles.vertex.stroke : handles.midpoint.stroke
            });
            this.visual.append(handleVisual);
            this.handles.push({
                visual: handleVisual,
                point: pt
            });
        });
    }
    _getCursor(point) {
        const handle = point && this._hitTest(point);
        if (handle) {
            return Cursors.arrow;
        }
    }
    _hitTest(point) {
        var _a;
        const handleOptions = this.options;
        if (!handleOptions.midpoint || !handleOptions.vertex) {
            return null;
        }
        const hitDistance = handleOptions.midpoint.radius + (((_a = handleOptions.midpoint.stroke) === null || _a === void 0 ? void 0 : _a.width) || 1);
        // Scale hit distance by zoom level (handle sizes are in layer pixels, but we compare in model space)
        const zoom = this.diagram.zoom();
        const scaledHitDistance = hitDistance / zoom;
        return this.handles.find((h) => h.point.distanceTo(point) <= scaledHitDistance) || null;
    }
    start(p) {
        this.activeHandle = this._hitTest(p);
        if (this.activeHandle) {
            this._initialPoints = this.connection.points().map(pt => pt.clone());
            this._firstMove = true;
        }
    }
    move(_handle, p) {
        if (!this.activeHandle) {
            return false;
        }
        const all = this.connection.allPoints();
        const intermediate = all.slice(1, -1);
        const point = this.activeHandle.point;
        const handlePointIndex = this._handlePoints.findIndex(hp => hp.point.equals(point));
        const handlePoint = this._handlePoints[handlePointIndex];
        if (!handlePoint) {
            return;
        }
        // Apply snap to point position
        const snappedPoint = this._applySnap(p, handlePointIndex);
        if (handlePoint.vertex) {
            const refPoint = intermediate.find(pt => pt.equals(handlePoint.point));
            if (refPoint) {
                refPoint.x = snappedPoint.x;
                refPoint.y = snappedPoint.y;
                this.connection.points(intermediate.map(pt => pt.clone()));
                this.updateConnection();
                // Update the handle point and visual position
                this.activeHandle.point = refPoint.clone();
                handlePoint.point = refPoint.clone();
            }
        }
        else {
            const prev = this._handlePoints[handlePointIndex - 1];
            const next = this._handlePoints[handlePointIndex + 1];
            if (prev) {
                const refPointindex = intermediate.findIndex(pt => pt.equals(prev.point));
                if (refPointindex >= 0) {
                    intermediate.splice(refPointindex + 1, 0, snappedPoint.clone());
                }
            }
            else if (next) {
                const refPointindex = intermediate.findIndex(pt => pt.equals(next.point));
                if (refPointindex >= 0) {
                    intermediate.splice(refPointindex, 0, snappedPoint.clone());
                }
            }
            else if (intermediate.length === 0) {
                intermediate.push(snappedPoint.clone());
            }
            this.connection.points(intermediate.map(pt => pt.clone()));
            this.updateConnection();
            // Recalculate handle points after adding new point
            this._handlePoints = this.createHandlePoints();
            // Find the new active handle position (it's now a vertex point at position snappedPoint)
            const newHandleIndex = this._handlePoints.findIndex(hp => hp.vertex && hp.point.equals(snappedPoint));
            if (newHandleIndex >= 0) {
                this.activeHandle.point = this._handlePoints[newHandleIndex].point;
            }
        }
        this.connection.refresh();
        return true;
    }
    updateConnection() {
        const source = this.connection.sourcePoint();
        const target = this.connection.targetPoint();
        const points = this.connection.points();
        // Manually update the path without full refresh (to avoid re-routing)
        this.connection.path.redraw({
            points: [source, ...points, target]
        });
        this.connection._alignContent();
        // Hide handles during drag for cleaner UX
        if (this._firstMove && this.handles) {
            this.handles.forEach((handle) => {
                if (handle.visual) {
                    handle.visual.visible(false);
                }
            });
        }
        this._firstMove = false;
    }
    /**
     * Apply snap to point position based on adjacent points
     * @param p - The current point position
     * @param handlePointIndex - The index of the handle being dragged
     * @returns The snapped point position
     */
    _applySnap(p, handlePointIndex) {
        const handlePoint = this._handlePoints[handlePointIndex];
        if (!handlePoint) {
            return p;
        }
        const snapThreshold = this.options.snap !== undefined ? this.options.snap : CONNECTION_POINTS_EDITING_SNAP;
        const all = this.connection.allPoints();
        let prevPoint = null;
        let nextPoint = null;
        if (handlePoint.vertex) {
            // Find the index of this vertex in all points
            const pointIndex = all.findIndex(pt => pt.distanceTo(handlePoint.point) < 0.1);
            if (pointIndex >= 0) {
                prevPoint = pointIndex > 0 ? all[pointIndex - 1] : null;
                nextPoint = pointIndex < all.length - 1 ? all[pointIndex + 1] : null;
            }
        }
        else {
            // For midpoint, find adjacent points from handle points
            const prev = this._handlePoints[handlePointIndex - 1];
            const next = this._handlePoints[handlePointIndex + 1];
            prevPoint = (prev === null || prev === void 0 ? void 0 : prev.point) || null;
            nextPoint = (next === null || next === void 0 ? void 0 : next.point) || null;
        }
        let snappedX = null;
        let snappedY = null;
        // Try to snap to horizontal line (same Y as prev or next)
        if (prevPoint) {
            const distY = Math.abs(p.y - prevPoint.y);
            if (distY < snapThreshold) {
                snappedY = prevPoint.y;
            }
        }
        if (nextPoint && snappedY === null) {
            const distY = Math.abs(p.y - nextPoint.y);
            if (distY < snapThreshold) {
                snappedY = nextPoint.y;
            }
        }
        // Try to snap to vertical line (same X as prev or next)
        if (prevPoint) {
            const distX = Math.abs(p.x - prevPoint.x);
            if (distX < snapThreshold) {
                snappedX = prevPoint.x;
            }
        }
        if (nextPoint && snappedX === null) {
            const distX = Math.abs(p.x - nextPoint.x);
            if (distX < snapThreshold) {
                snappedX = nextPoint.x;
            }
        }
        return new Point(snappedX !== null ? snappedX : p.x, snappedY !== null ? snappedY : p.y);
    }
    stop(_p) {
        if (!this.activeHandle) {
            return undefined;
        }
        const unit = new ConnectionDragUnit({
            item: this.connection,
            undoLocks: [],
            redoLocks: [],
            undoPoints: this._initialPoints,
            redoPoints: this.connection.points(),
            routingMode: 'manual',
            sourceSide: null,
            targetSide: null
        });
        // Clear active handle BEFORE refresh() to prevent it from being reassigned
        this._resetFlags();
        // Refresh handles to recreate them based on the new connection shape
        this.refresh();
        return unit;
    }
    refresh() {
        var _a;
        // Save the active handle point before recreating handles
        const activePoint = (_a = this.activeHandle) === null || _a === void 0 ? void 0 : _a.point.clone();
        this._createHandles();
        // Restore active handle reference after recreation
        if (activePoint) {
            this.activeHandle = this.handles.find(h => h.point.equals(activePoint)) || null;
        }
    }
    cancel() {
        if (this.activeHandle && this._initialPoints) {
            // Restore initial locks
            if (this._initialPoints) {
                this.connection.points(this._initialPoints);
                this.connection.refresh();
            }
            this.refresh();
            this._resetFlags();
        }
    }
    _resetFlags() {
        this.activeHandle = null;
        this._firstMove = false;
        // Clean up initial points reference
        if (this._initialPoints) {
            this._initialPoints = undefined;
        }
    }
}
